<template>
    <div v-if="docs" class="injuries-section">
        <div class="les-toolbar">
            <div class="les-search">
                <v-icon size="16">mdi-magnify</v-icon>
                <input type="text" :placeholder="$t('common.search')" :value="search"
                    @input="search = $event.target.value">
            </div>
            <button-tooltip bottom :tooltip="$t('tooltips.filter')" class="ml-auto">
                <template #activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" icon class="les-icon-btn" :class="{ active: !!filterInjuries }"
                        @click="showFilterInjuries">
                        <v-icon size="18">mdi-filter-outline</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
            <button-tooltip bottom :tooltip="$t('tooltips.active_injuries')">
                <template #activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" text class="les-active-btn" :class="{ active: activeOnly }"
                        @click="activeOnly = !activeOnly">
                        <span class="les-active-dot" />{{ $t('statistics.active_injuries') }}
                    </v-btn>
                </template>
            </button-tooltip>
        </div>

        <stats-grid :count="6" :expanded="expandedCard" :row-height="300"
            :expanded-row-height="460">
            <stat-card expandable :expanded="expandedCard === 0" @toggle:expand="toggleExpand(0)">
                <template #header>
                    <span>{{ $t('common.quantity') }}</span>
                    <kimsa-combobox class="inj-metric-select" dense solo no-object :items="quantityOptions"
                        :value="quantityType" @change="quantityType = $event" height="30" />
                </template>
                <quantity-chart :docs="enrichedDocs" :type="quantityType" />
            </stat-card>
            <stat-card expandable :expanded="expandedCard === 1" @toggle:expand="toggleExpand(1)">
                <template #header>{{ $t('common.injury_type') }}</template>
                <injury-type-chart :docs="enrichedDocs" />
            </stat-card>
            <stat-card expandable :expanded="expandedCard === 2" @toggle:expand="toggleExpand(2)">
                <template #header>{{ $t('common.zone') }}</template>
                <injury-zone-chart :docs="enrichedDocs" />
            </stat-card>
            <stat-card expandable :expanded="expandedCard === 3" @toggle:expand="toggleExpand(3)">
                <template #header>{{ $t('common.condition') }}</template>
                <injury-complexity-chart :docs="enrichedDocs" @select="onSelectComplexity" />
            </stat-card>
            <stat-card expandable :expanded="expandedCard === 4" @toggle:expand="toggleExpand(4)">
                <template #header>{{ $t('common.moment') }}</template>
                <injury-moment-chart :docs="enrichedDocs" @select="onSelectMoment" />
            </stat-card>
            <stat-card expandable :expanded="expandedCard === 5" @toggle:expand="toggleExpand(5)">
                <template #header>{{ $t('common.particularity') }}</template>
                <injury-condition-chart :docs="enrichedDocs" />
            </stat-card>
        </stats-grid>

        <div class="injuries-table-section">
            <div class="inj-table-wrap">
                <table class="inj-table">
                    <thead>
                        <tr>
                            <sortable-th :label="$t('common.name')" sort-key="name" :sort-by="sortBy" :desc="sortDesc"
                                @sort="onSort" />
                            <sortable-th :label="$t('common.condition')" sort-key="severityRank" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <sortable-th :label="$t('common.injury')" sort-key="typeLabel" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <sortable-th :label="$t('common.zone')" sort-key="zoneLabel" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <sortable-th :label="$t('common.date')" sort-key="dateSort" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <sortable-th :label="$t('common.discharge')" sort-key="dischargeSort" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <sortable-th :label="$t('common.observation')" sort-key="comment" :sort-by="sortBy"
                                :desc="sortDesc" @sort="onSort" />
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in pagedInjuries" :key="item.id" :class="{ 'row-active': item.dischargeActive }">
                            <td class="td-name">{{ item.name }}</td>
                            <td>
                                <injury-badge :complexity="item.complexity" />
                            </td>
                            <td>{{ item.typeLabel }}</td>
                            <td>{{ item.zoneLabel }}</td>
                            <td>{{ item.dateString }}</td>
                            <td :class="{ 'td-alta-active': item.dischargeActive }">{{ item.dischargeDate }}</td>
                            <td class="td-obs">
                                <truncated-text :text="item.comment || '—'" />
                            </td>
                            <td class="td-actions">
                                <button-tooltip bottom :tooltip="$t('common.edit')">
                                    <template #activator="{ on, attrs }">
                                        <v-btn v-on="on" v-bind="attrs" icon class="inj-edit-btn"
                                            @click="onDetailsAction(item.id)">
                                            <v-icon size="15">mdi-pencil-outline</v-icon>
                                        </v-btn>
                                    </template>
                                </button-tooltip>
                            </td>
                        </tr>
                        <tr v-if="pagedInjuries.length === 0">
                            <td colspan="8" class="inj-empty">{{ $t('common.no_data_yet') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <kimsa-pagination v-if="pageCount > 1" @page="page = $event" :pageCount="pageCount" :value="page" />
        </div>

        <injury-form-dialog :studentId="studentId" ref="details" @success="onEditInjury" />
        <filter-injuries-dialog ref="filter" @filter="onFilterInjuries" />
        <injury-details-dialog ref="injuryDetails" />
    </div>
</template>

<script>
import { safeLower } from '@/utils/Common'
import { injuryDate, isActiveInjury, injurySeverity } from '@/utils/Injuries'
import FilterInjuriesDialog from '../common/FilterInjuriesDialog.vue'
import KimsaPagination from '../common/KimsaPagination.vue'
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue'
import ButtonTooltip from '../common/ButtonTooltip.vue'
import TruncatedText from '../common/TruncatedText.vue'
import SortableTh from '../common/SortableTh.vue'
import InjuryBadge from '../common/InjuryBadge.vue'
import InjuryFormDialog from '../student/InjuryFormDialog.vue'
import StatCard from './StatCard.vue'
import StatsGrid from './StatsGrid.vue'
import InjuryComplexityChart from './injuries/InjuryComplexityChart.vue'
import InjuryConditionChart from './injuries/InjuryConditionChart.vue'
import InjuryDetailsDialog from './injuries/InjuryDetailsDialog.vue'
import InjuryMomentChart from './injuries/InjuryMomentChart.vue'
import InjuryTypeChart from './injuries/InjuryTypeChart.vue'
import InjuryZoneChart from './injuries/InjuryZoneChart.vue'
import QuantityChart from './injuries/QuantityChart.vue'
export default {
    components: {
        FilterInjuriesDialog,
        KimsaPagination,
        KimsaCombobox,
        ButtonTooltip,
        TruncatedText,
        SortableTh,
        InjuryBadge,
        InjuryFormDialog,
        StatCard,
        StatsGrid,
        QuantityChart,
        InjuryTypeChart,
        InjuryZoneChart,
        InjuryConditionChart,
        InjuryComplexityChart,
        InjuryMomentChart,
        InjuryDetailsDialog,
    },
    props: {
        docs: {
            type: Array,
            default: () => []
        },
        teamStudents: {
            type: Array,
            default: () => []
        }
    },
    data(vm) {
        return {
            search: '',
            page: 1,
            perPage: 10,
            filterInjuries: null,
            activeOnly: false,
            sortBy: null,
            sortDesc: false,
            expandedCard: null,
            studentId: null,
            quantityType: 'total_injuries',
            quantityOptions: [
                { name: vm.$t('common.total_injuries'), value: 'total_injuries' },
                { name: vm.$t('common.injury_very_high'), value: 'injury_very_high' },
                { name: vm.$t('common.injury_high'), value: 'injury_high' },
                { name: vm.$t('common.injury_medium'), value: 'injury_medium' },
                { name: vm.$t('common.injury_low'), value: 'injury_low' },
                { name: vm.$t('common.injury_very_low'), value: 'injury_very_low' },
            ],
        }
    },
    computed: {
        enrichedDocs(vm = this) {
            return vm.docs.map(doc => {
                const date = injuryDate(doc.date)
                const discharge = injuryDate(doc.dischargeDate)
                return {
                    ...doc,
                    dateString: date ? date.format('DD-MM-YYYY') : '—',
                    dateSort: date ? date.valueOf() : 0,
                    dischargeActive: discharge ? discharge.isAfter(vm.$moment(), 'day') : false,
                    dischargeDate: discharge ? discharge.format('DD-MM-YYYY') : '—',
                    rawDischargeDate: doc.dischargeDate,
                    dischargeSort: discharge ? discharge.valueOf() : 0,
                    active: isActiveInjury(doc),
                    name: vm.getStudentName(doc.studentId),
                    typeLabel: vm.injuryTypeLabel(doc.type),
                    zoneLabel: vm.injuryZoneLabel(doc.zone),
                    complexityLabel: vm.injuryComplexityLabel(doc.complexity),
                    severityRank: injurySeverity(doc.complexity).rank,
                }
            })
        },
        injuries(vm = this) {
            let injuries = vm.enrichedDocs
            if (vm.filterInjuries) {
                injuries = injuries.filter(doc => {
                    return (!vm.filterInjuries.startDate || doc.rawDischargeDate >= vm.filterInjuries.startDate) &&
                        (!vm.filterInjuries.endDate || doc.rawDischargeDate <= vm.filterInjuries.endDate) &&
                        (!vm.filterInjuries.type || doc.type === vm.filterInjuries.type) &&
                        (!vm.filterInjuries.zone || doc.zone === vm.filterInjuries.zone) &&
                        (!vm.filterInjuries.condition || doc.condition === vm.filterInjuries.condition) &&
                        (!vm.filterInjuries.wasIt || doc.wasIt === vm.filterInjuries.wasIt)
                })
            }
            if (vm.activeOnly) injuries = injuries.filter(doc => doc.active)
            return injuries
        },
        filteredInjuries(vm = this) {
            if (!vm.search) return vm.injuries
            const query = vm.search.toLowerCase()
            return vm.injuries.filter(injury =>
                safeLower(injury.name).includes(query) ||
                safeLower(injury.typeLabel).includes(query) ||
                safeLower(injury.zoneLabel).includes(query) ||
                safeLower(injury.complexityLabel).includes(query) ||
                safeLower(injury.comment).includes(query)
            )
        },
        sortedInjuries(vm = this) {
            if (!vm.sortBy) return vm.filteredInjuries
            const direction = vm.sortDesc ? -1 : 1
            return [...vm.filteredInjuries].sort((a, b) => {
                const first = a[vm.sortBy]
                const second = b[vm.sortBy]
                if (typeof first === 'number' && typeof second === 'number') return direction * (first - second)
                return direction * String(first || '').localeCompare(String(second || ''), undefined, { sensitivity: 'base' })
            })
        },
        pageCount(vm = this) {
            return vm.sortedInjuries.length > 0 ? Math.ceil(vm.sortedInjuries.length / vm.perPage) : 1
        },
        pagedInjuries(vm = this) {
            const start = (vm.page - 1) * vm.perPage
            return vm.sortedInjuries.slice(start, start + vm.perPage)
        },
    },
    methods: {
        toggleExpand(index) {
            const vm = this
            vm.expandedCard = vm.expandedCard === index ? null : index
        },
        onSort(sortKey) {
            const vm = this
            if (vm.sortBy === sortKey) vm.sortDesc = !vm.sortDesc
            else {
                vm.sortBy = sortKey
                vm.sortDesc = false
            }
        },
        onSelectComplexity(bar) {
            const vm = this
            vm.$refs.injuryDetails.show({
                label: `${vm.$t('common.condition')}: ${bar.label}`,
                color: bar.color,
                items: bar.items,
            })
        },
        onSelectMoment(bar) {
            const vm = this
            vm.$refs.injuryDetails.show({
                label: `${vm.$t('common.moment')}: ${bar.label}`,
                color: bar.color,
                items: bar.items,
            })
        },
        onDetailsAction(id) {
            const vm = this;
            const injury = vm.enrichedDocs.find(doc => doc.id === id)

            vm.currentInjury = {
                ...injury,
                dischargeDate: injury.dischargeDate && injury.dischargeDate !== '—' ? vm.$moment(injury.dischargeDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '-',
            }
            vm.studentId = vm.currentInjury.studentId
            vm.$refs.details.loadInjury(vm.currentInjury)
        },
        onEditInjury() {
            const vm = this;
            vm.$emit('reloadDocs')
            vm.currentInjury = null
            vm.studentId = null
        },
        showFilterInjuries() {
            const vm = this;
            vm.$refs.filter.show(vm.filterInjuries)
        },
        onFilterInjuries(filter = null) {
            const vm = this;
            vm.filterInjuries = filter
        },
        getStudentName(studentId) {
            const vm = this;
            const student = vm.teamStudents.find(s => s.objectID === studentId) || {}
            return student.name
        }
    },
    watch: {
        search() {
            this.page = 1
        },
        filterInjuries() {
            this.page = 1
        },
        activeOnly() {
            this.page = 1
        }
    }
}
</script>

<style scoped>
.injuries-section {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
}

.les-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0 14px;
}

.les-search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 12px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    flex: 1 1 180px;
    max-width: 260px;
    color: var(--v-txtMuted-base);
}

.les-search input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    font-family: inherit;
    color: var(--v-txtPrimary-base);
}

.les-search input::placeholder {
    color: var(--v-txtMuted-base);
}

.les-icon-btn.v-btn {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
}

.les-icon-btn.v-btn.active {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base) !important;
    background: rgba(204, 255, 51, 0.08) !important;
}

.les-active-btn.v-btn {
    height: 38px;
    min-width: 0;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
}

.les-active-btn.v-btn.active {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base) !important;
    background: rgba(204, 255, 51, 0.08) !important;
}

.les-active-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
    background: currentColor;
}

.inj-metric-select {
    flex: 0 0 auto;
    max-width: 170px;
}

.inj-metric-select ::v-deep .v-input,
.inj-metric-select ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.inj-metric-select ::v-deep .v-input__control,
.inj-metric-select ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.inj-metric-select ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.inj-metric-select ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.inj-metric-select ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.injuries-table-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 420px;
    margin-top: 16px;
}

.inj-table-wrap {
    overflow-x: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}

.inj-table {
    width: 100%;
    border-collapse: collapse;
}

.inj-table ::v-deep thead th {
    background: var(--v-surface2-base);
    padding: 10px 14px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    text-align: left;
    white-space: nowrap;
}

.inj-table tbody tr {
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.12s;
}

.inj-table tbody tr:last-child {
    border-bottom: none;
}

.inj-table tbody tr:hover {
    background: var(--v-surface2-base);
}

.inj-table tbody tr.row-active {
    background: rgba(232, 92, 92, 0.05);
}

.inj-table td {
    padding: 11px 14px;
    font-size: 12px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    vertical-align: middle;
}

.inj-table td.td-name {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.inj-table td.td-alta-active {
    color: var(--v-error-base);
    font-weight: 600;
}

.inj-table td.td-obs {
    max-width: 160px;
}

.inj-table td.td-actions {
    text-align: right;
    width: 1%;
    white-space: nowrap;
}

.inj-edit-btn.v-btn {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
}

.inj-edit-btn.v-btn:hover {
    color: var(--v-txtPrimary-base) !important;
    border-color: var(--v-outline2-base);
}

.inj-empty {
    text-align: center;
    padding: 32px;
    color: var(--v-txtMuted-base);
    font-size: 13px;
}
</style>
